/**
 * Where an in-bundle navigation should take the reader, expressed as the
 * route's German document slug — the only kind of slug `/dokumentation/:slug`
 * and the `documents` table understand.
 *
 * DocFrame reports the slug it reads back from the iframe's own URL, which is
 * just the loaded HTML file's basename. That is a *bundle* slug: for a German
 * bundle it is already the document slug, but for an English bundle it is the
 * `<slug>-en` topic (see englishSlugFor in the API's translations.ts, the one
 * place that pairing convention is defined). Two situations report a bundle
 * slug that differs from the document currently on screen:
 *
 *  - The frame's very first load, reporting the page that was just asked for.
 *    This must not cause a navigation at all — comparing directly against the
 *    document's own English slug (`translation.slug`, computed server-side by
 *    that same englishSlugFor) settles this exactly, with no client-side
 *    guess about what the convention is. Getting this wrong is the English
 *    documentation's "opening any document renders a 404" bug: the frame
 *    reports `<slug>-en`, that is mistaken for a different document, and the
 *    app navigates to a topic slug the (German-only) documents table has
 *    never heard of.
 *  - A same-bundle link to a genuinely different topic. There the document
 *    does change, and must change to that topic's *German* slug so the
 *    reader keeps reading in the locale they asked for (the page re-fetches
 *    at the current locale, not the German original). No index of every
 *    English-to-German pairing is available on the client, so this one
 *    direction — recovering a German slug from some other topic's English
 *    one — still applies the `-en` suffix rule locally. That is the one piece
 *    of the convention this module duplicates, and only for topics other
 *    than the one already open.
 */
export function navigationTargetFor(
  frameSlug: string,
  currentSlug: string,
  englishSlug: string | null,
): string | null {
  if (!frameSlug || frameSlug === currentSlug || frameSlug === englishSlug) return null;
  const target = frameSlug.endsWith("-en") ? frameSlug.slice(0, -3) : frameSlug;
  return target !== currentSlug ? target : null;
}
